import { IconStarFilled } from "@tabler/icons-react";

import { useTourContext } from "@/contexts";
import { useT } from "@/i18n";
import { trackAction } from "@/lib/sentry";
import {
  DialogTrigger,
  Dropdown,
  DropdownItem,
  DropdownSection,
  IconButton,
  IconPlus,
  NavLinkGroup,
} from "@/ui";

import { NavLink, NavLinkIcon, useGetNavLinkLabel, useGetNavLinksGroupLabel } from "../NavLink";
import { useNavLinkGroups } from "../NavLink/useNavLinkGroups";

import { useNavLinksState } from "./useNavLinksState";

export const Favorites = () => {
  const getNavLinksGroupLabel = useGetNavLinksGroupLabel();
  const getNavLinkLabel = useGetNavLinkLabel();
  const navLinkGroups = useNavLinkGroups();
  const tourContext = useTourContext();
  const t = useT();

  const {
    favorites,
    isFavorite,
    addToFavorites,
    moveFavoriteUp,
    moveFavoriteDown,
    removeFromFavorites,
  } = useNavLinksState();

  return (
    <div id={tourContext.ids.favorites}>
      <NavLinkGroup
        color="brand"
        icon={IconStarFilled}
        title={t("general.navigation.favorites.title")}
        id="favorites"
        actions={
          <DialogTrigger>
            <IconButton
              size="xs"
              icon={IconPlus}
              hideTooltip={true}
              label={t("general.components.iconButton.labels.moreActions")}
              className="text-new-primary"
            />
            <Dropdown label={t("general.components.dropdown.labels.actions")}>
              {navLinkGroups
                .map((group) => ({
                  ...group,
                  items: group.items.filter((item) => !isFavorite(item.type)),
                }))
                .filter((group) => group.items.length > 0)
                .map((group) => (
                  <DropdownSection
                    key={group.type}
                    title={getNavLinksGroupLabel(group.type).toLocaleUpperCase()}
                  >
                    {group.items.map((item) => (
                      <DropdownItem
                        key={item.type}
                        label={getNavLinkLabel(item.type)}
                        renderStartIcon={(props) => <NavLinkIcon type={item.type} {...props} />}
                        onAction={() => {
                          trackAction("dashboard.sidebar.favorites.add", {
                            navLinkType: item.type,
                          });
                          addToFavorites(item);
                        }}
                      />
                    ))}
                  </DropdownSection>
                ))}
            </Dropdown>
          </DialogTrigger>
        }
      >
        {favorites.length > 0 ? (
          favorites.map((item, index, array) => (
            <NavLink
              key={item.type}
              item={item}
              actions={[
                {
                  type: "moveUp",
                  onClick: () => moveFavoriteUp(item.type),
                  disabled: index === 0,
                },
                {
                  type: "moveDown",
                  onClick: () => moveFavoriteDown(item.type),
                  disabled: index === array.length - 1,
                },
                {
                  type: "removeFromFavorites",
                  onClick: () => {
                    trackAction("dashboard.sidebar.favorites.remove", {
                      navLinkType: item.type,
                    });
                    removeFromFavorites(item);
                  },
                  hidden: !isFavorite(item.type),
                },
              ]}
            />
          ))
        ) : (
          <div className="preset-body-md text-secondary pl-2">
            {t("general.navigation.favorites.noFavorites")}
          </div>
        )}
      </NavLinkGroup>
    </div>
  );
};
